// src/auth/ProtectedRoute.jsx
import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaHome, FaLock } from "react-icons/fa";
import { useAuth } from "./AuthContext";
import { isOrgAdmin, isSuperadmin } from "../../utils/roles";

function parseJwt(token) {
  try {
    const base64 = token.split(".")[1];
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function isExpired(token) {
  const payload = parseJwt(token);
  if (!payload?.exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec;
}

const ProtectedRoute = ({ children, permission, adminOnly = false, superadminOnly = false }) => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const hasNotifiedRef = useRef(false);

  const expired = token ? isExpired(token) : false;
  const isAuthenticated = Boolean(user && token && !expired);

  useEffect(() => {
    if (!isAuthenticated) {
      const hadSession = Boolean(user || token);
      if (hadSession && !hasNotifiedRef.current) {
        hasNotifiedRef.current = true;
        toast.error("Votre session a expiré. Veuillez vous reconnecter.");
      }
      if (user || token) {
        logout();
      }
    } else {
      hasNotifiedRef.current = false;
    }
  }, [isAuthenticated, user, token, logout]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Réservé superadmin (console SaaS) : org_admin refusé.
  if (superadminOnly) {
    return isSuperadmin(user) ? children : <AccessDenied />;
  }

  // Permissions
  if (isOrgAdmin(user)) return children;

  // Réservé admin : un non-admin est refusé même s'il a la permission de base.
  if (adminOnly) return <AccessDenied />;

  const userPerms = user.permissions || [];
  if (permission && !userPerms.includes(permission)) {
    return <AccessDenied />;
  }

  return children;
};

function AccessDenied() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] p-8 text-center animate-fade-in">
      <div className="w-20 h-20 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
        <FaLock className="text-3xl text-red-500" />
      </div>
      <h2 className="text-2xl font-extrabold text-base-content mb-2">{t("user.accessDenied")}</h2>
      <p className="text-base-content/60 text-sm max-w-sm mb-1">{t("user.accessDeniedMsg")}</p>
      <p className="text-base-content/40 text-xs max-w-sm mb-6">{t("user.accessDeniedSub")}</p>
      <Link to="/" className="btn btn-primary btn-sm gap-2">
        <FaHome size={14} /> {t("user.goHome")}
      </Link>
    </div>
  );
}

export default ProtectedRoute;
